import React, { useState, useMemo } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import DashboardPage from "./DashboardPage"; // Path to your IEX file in the same folder
import { Link } from "react-router-dom";
import iex from "./iex";
import * as XLSX from "xlsx";
import logo from "./logo.png";
import { 
  ComposedChart, Bar, Line, Tooltip, Legend, XAxis, YAxis, 
  CartesianGrid, ResponsiveContainer, ReferenceLine, Cell 
} from "recharts";
import { calcDSM_0_10, calcDSM_10_15, calcDSM_15 } from "../utils/dsmCalculations";

/* ======================= 
 Helpers 
======================= */
const formatDate = (v) => {
  if (!v) return "";
  let d;
  if (typeof v === "number") d = new Date(Math.round((v - 25569) * 86400 * 1000));
  else if (typeof v === "string") return v;
  else return "";
  if (d instanceof Date && !isNaN(d)) {
    return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
  }
  return "";
};

const formatINR = (num) => new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2, style: 'currency', currency: 'INR' }).format(num);
const isMismatch = (wrpc, ours) => Math.abs(Number(wrpc || 0) - Number(ours || 0)) > 1;

export default function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState([]);
  const [unit, setUnit] = useState("MW");

  const handleUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const wb = XLSX.read(new Uint8Array(evt.target.result), { type: "array" });
      const rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { header: 1 }).slice(1);
      const processed = rows.map((r, i) => {
        const actMwh = parseFloat(r[5]) || 0;
        const schMwh = parseFloat(r[6]) || 0;
        const freq = Number(r[3] || 50);
        const NR = Number(r[12] || 0);
        const d1 = calcDSM_0_10(actMwh, schMwh, freq, NR) || 0;
        const d2 = calcDSM_10_15(actMwh, schMwh, freq, NR) || 0;
        const d3 = calcDSM_15(actMwh, schMwh, freq, NR) || 0;
        const totalDSM = d1 + d2 + d3;
        
        return {
          id: i, date: formatDate(r[0]), time: r[1], block: r[2], freq,
          sch: schMwh, act: actMwh, dev: actMwh - schMwh,
          devPct: schMwh === 0 ? 0 : ((actMwh - schMwh) / schMwh) * 100,
          d1, d2, d3, nr: NR,
          rate: Math.abs(actMwh - schMwh) === 0 ? 0 : Math.abs(totalDSM / (Math.abs(actMwh - schMwh) * 1000)),
          wrpcPay: Number(r[10] || 0), wrpcRec: Number(r[11] || 0),
          ourPay: totalDSM < 0 ? Math.abs(totalDSM) : 0, oRec: totalDSM > 0 ? totalDSM : 0,
        };
      }).filter(Boolean);
      setData(processed);
    };
    reader.readAsArrayBuffer(file);
  };

  const pivot = useMemo(() => {
    const grouped = data.reduce((acc, d) => {
      acc[d.date] ??= { date: d.date, p: 0, r: 0, schMwh: 0, actMwh: 0,overDrawal: 0,underDrawal: 0,blks: 0, pWtd: 0, rWtd: 0, pDev: 0, rDev: 0, fSum: 0 };
      acc[d.date].p += d.ourPay; acc[d.date].r += d.oRec;
      acc[d.date].schMwh += d.sch; acc[d.date].actMwh += d.act;
      if (d.dev > 0) {
  acc[d.date].overDrawal += d.dev;
}

if (d.dev < 0) {
  acc[d.date].underDrawal += Math.abs(d.dev);
}
      acc[d.date].blks += 1; acc[d.date].fSum += d.freq;
      const absDev = Math.abs(d.dev);
      if (d.ourPay > 0) { acc[d.date].pWtd += absDev * d.rate; acc[d.date].pDev += absDev; }
      if (d.oRec > 0) { acc[d.date].rWtd += absDev * d.rate; acc[d.date].rDev += absDev; }
      return acc;
    }, {});
    return Object.values(grouped).map(v => ({
      ...v,
      avgPayRate: v.pDev === 0 ? 0 : v.pWtd / v.pDev,
      avgRecRate: v.rDev === 0 ? 0 : v.rWtd / v.rDev,
      avgFreq: v.fSum / v.blks,
      netLakh: Number(((v.r - v.p) / 100000).toFixed(2))
    }));
  }, [data]);

  const global = useMemo(() => {
    return pivot.reduce((acc, v) => {
      acc.p += v.p; acc.r += v.r; acc.sch += v.schMwh; acc.act += v.actMwh;acc.over += v.overDrawal || 0;
acc.under += v.underDrawal || 0;
      acc.blks += v.blks; acc.pWtd += v.pWtd; acc.rWtd += v.rWtd;
      acc.pDev += v.pDev; acc.rDev += v.rDev;
      return acc;
    }, { p: 0, r: 0, sch: 0, act: 0, over: 0,under: 0,blks: 0, pWtd: 0, rWtd: 0, pDev: 0, rDev: 0 });
  }, [pivot]);

  const factor = unit === "MW" ? 4 : 1;

  return (
    <div className="min-h-screen bg-slate-50 p-4 space-y-6 text-sm">
      <nav className="flex justify-between items-center p-3 bg-white shadow rounded-lg sticky top-0 z-50">
        <div className="font-bold text-indigo-700 flex items-center gap-2 text-lg">
          <img src={logo} alt="Logo" className="h-6" /> DSM ANALYTICS
        </div>
        
        <div className="flex items-center gap-6">
          <div className="flex gap-4 font-bold text-xs uppercase tracking-wider">

  <Link 
    to="/"
    className={location.pathname === "/" 
      ? "text-indigo-600 border-b-2 border-indigo-600" 
      : "text-slate-400 hover:text-indigo-600"}
  >
    Dashboard
  </Link>

  <Link 
    to="/DashboardPage"
    className={location.pathname === "/DashboardPage" 
      ? "text-indigo-600 border-b-2 border-indigo-600" 
      : "text-slate-400 hover:text-indigo-600"}
  >
    Hybrid Power
  </Link>

  <Link 
    to="/iex"
    className={location.pathname === "/iex" 
      ? "text-indigo-600 border-b-2 border-indigo-600" 
      : "text-slate-400 hover:text-indigo-600"}
  >
    IEX LIVE
  </Link>

</div>
            
          <div className="flex items-center gap-4">
            <div className="flex bg-slate-200 p-1 rounded-full cursor-pointer relative w-24 h-8 select-none border" onClick={() => setUnit(u => u === "MW" ? "MWh" : "MW")}>
               <div className={`absolute w-11 h-6 bg-indigo-600 rounded-full transition-all duration-300 ${unit === "MW" ? "left-1" : "left-12"}`} />
               <div className={`z-10 w-11 text-center font-black text-[10px] leading-6 ${unit === "MW" ? "text-white" : "text-slate-500"}`}>MW</div>
               <div className={`z-10 w-11 text-center font-black text-[10px] leading-6 ${unit === "MWh" ? "text-white" : "text-slate-500"}`}>MWh</div>
            </div>
            <input type="file" onChange={handleUpload} className="text-xs" />
          </div>
        </div>
      </nav>

      <Routes>
        <Route path="/" element={
          data.length > 0 ? (
            <>
              {/* MAIN TABLE */}
              <div className="bg-white rounded shadow overflow-hidden">
                <div className="overflow-x-auto max-h-[30vh]">
                  <table className="w-full text-left table-fixed border-collapse text-sm">
                    <thead className="bg-slate-800 text-white sticky top-0 uppercase text-[10px]">
                      <tr className="divide-x divide-slate-700">
                        <th className="w-24 p-2">Date</th><th className="w-16">Time</th><th className="w-12">Blk</th><th className="w-16">Freq</th>
                        <th className="w-24">Sch({unit})</th><th className="w-24">Act({unit})</th><th className="w-24">Dev({unit})</th><th className="w-16">Dev%</th>
                        <th className="w-14">0-10</th><th className="w-14">10-15</th><th className="w-14">>15</th>
                        <th className="w-20">NR(Rs/kWh)</th><th className="w-20">Avg Rate</th>
                        <th className="w-20">WRPC Pay</th><th className="w-20">WRPC Rec</th><th className="w-20 bg-indigo-900">Our Pay</th><th className="w-20 bg-indigo-900">Our Rec</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y text-slate-700 text-[12px]">
                      {data.map((d, i) => (
                        <tr key={i} className="hover:bg-slate-50 divide-x divide-slate-100">
                          <td className="p-1">{d.date}</td><td>{d.time}</td><td>{d.block}</td><td>{d.freq.toFixed(2)}</td>
                          <td>{(d.sch * factor).toFixed(2)}</td><td>{(d.act * factor).toFixed(2)}</td>
                          <td className={`font-bold ${d.dev < 0 ? 'text-teal-500' : 'text-rose-600'}`}>{(d.dev * factor).toFixed(2)}</td>
                          <td>{d.devPct.toFixed(1)}%</td><td>{d.d1.toFixed(0)}</td><td>{d.d2.toFixed(0)}</td><td>{d.d3.toFixed(0)}</td>
                          <td className="text-indigo-600 font-bold">{(d.nr / 100).toFixed(4)}</td><td className="font-bold">{d.rate.toFixed(2)}</td>
                          <td>{d.wrpcPay}</td><td>{d.wrpcRec}</td>
                          <td className={isMismatch(d.wrpcPay, d.ourPay) ? 'bg-red-50 text-red-600 font-bold' : ''}>{d.ourPay.toFixed(0)}</td>
                          <td className="text-teal-600 font-bold">{d.oRec.toFixed(0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* DAILY PIVOT */}
              <div className="bg-white rounded shadow p-4">
                <h3 className="font-bold text-xs mb-3 uppercase opacity-50">Daily Summary Pivot</h3>
                <table className="w-full text-xs text-right border">
                  <thead className="bg-slate-50 border-b">
                    <tr><th className="text-left p-2">Date</th> <th>Sch MWh</th>
  <th>Act MWh</th>
  <th>Over Drawal</th>
  <th>Under Drawal</th><th>Payable(₹)</th><th>Receivable(₹)</th><th>Avg Pay Rate</th><th>Avg Rec Rate</th><th className="bg-indigo-50">Net(₹)</th></tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {pivot.map(r => (
                      <tr key={r.date} className="hover:bg-slate-50">
                        <td className="text-left p-2 font-medium">{r.date}</td><td>{r.schMwh.toFixed(2)}</td>
<td>{r.actMwh.toFixed(2)}</td>
<td className="text-rose-600 font-semibold">{r.overDrawal.toFixed(2)}</td>
<td className="text-teal-600 font-semibold">{r.underDrawal.toFixed(2)}</td><td>{r.p.toLocaleString()}</td><td>{r.r.toLocaleString()}</td>
                        <td className="text-rose-500">{r.avgPayRate.toFixed(2)}</td><td className="text-teal-600">{r.avgRecRate.toFixed(2)}</td>
                        <td className="font-bold bg-indigo-50">{(r.r - r.p).toFixed(0)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-slate-800 text-white font-bold border-t-2">
                    <tr>
                      <td className="p-2 text-left">TOTAL / AVG</td>
                    <td>{global.sch.toFixed(2)}</td>
                    <td>{global.act.toFixed(2)}</td>
                        <td>{global.over.toFixed(2)}</td>
                          <td>{global.under.toFixed(2)}</td>
                        <td>{global.p.toLocaleString()}</td><td>{global.r.toLocaleString()}</td>
                      <td>{(global.pWtd / (global.pDev || 1)).toFixed(2)}</td><td>{(global.rWtd / (global.rDev || 1)).toFixed(2)}</td>
                      <td className="bg-indigo-700 font-black">{formatINR(global.r - global.p)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* CHARTS & CARDS */}
              <div className="flex flex-col lg:flex-row gap-4 h-auto lg:h-[520px]">
                <div className="lg:w-2/3 space-y-4">
                  <div className="bg-white p-4 rounded-xl shadow-sm border h-[260px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={pivot}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="date" fontSize={10} /><YAxis yAxisId="L" fontSize={10} /><YAxis yAxisId="R" orientation="right" domain={[49.8, 50.2]} fontSize={10} />
                        <Tooltip formatter={(v) => v.toFixed(2)} /><Legend verticalAlign="top" />
                        <Line yAxisId="L" name="Avg Rec Rate" dataKey="avgRecRate" stroke="#0d9488" strokeWidth={3} dot={{r:3}} />
                        <Line yAxisId="L" name="Avg Pay Rate" dataKey="avgPayRate" stroke="#e11d48" strokeWidth={3} dot={{r:3}} />
                        <Line yAxisId="R" name="Avg Frequency" dataKey="avgFreq" stroke="#6366f1" strokeWidth={2} strokeDasharray="5 5" dot={false} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="bg-white p-4 rounded-xl shadow-sm border h-[220px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={pivot}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="date" fontSize={10} /><YAxis fontSize={10} />
                        <Tooltip formatter={(v) => v.toFixed(2) + " Lakhs"} /><ReferenceLine y={0} stroke="#000" strokeWidth={2} />
                        <Bar name="Net Lakhs" dataKey="netLakh">{pivot.map((e, idx) => <Cell key={idx} fill={e.netLakh >= 0 ? "#0d9488" : "#e11d48"} />)}</Bar>
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                <div className="lg:w-1/3 grid grid-cols-1 gap-2">
                  <div className="bg-slate-800 text-white p-4 rounded shadow border-l-4 border-indigo-400">
                    <span className="text-[9px] uppercase opacity-50 tracking-widest font-bold">Total Scheduled</span>
                    <span className="text-xl font-black block">{global.sch.toLocaleString()} MWh</span>
                  </div>
                  <div className="bg-slate-800 text-white p-4 rounded shadow border-l-4 border-rose-400">
                    <span className="text-[9px] uppercase opacity-50 tracking-widest font-bold">Total Actual</span>
                    <span className="text-xl font-black block">{global.act.toLocaleString()} MWh</span>
                  </div>
                  <div className="bg-indigo-900 text-white p-4 rounded shadow border-l-4 border-amber-400">
                    <span className="text-[9px] uppercase opacity-50 tracking-widest font-bold">Deviation Analysis</span>
                    <h4 className="text-xl font-black">{(global.act - global.sch).toFixed(2)} MWh <small className="opacity-60 text-xs">({(((global.act - global.sch)/global.sch)*100).toFixed(2)}%)</small></h4>
                  </div>
                  <div className="bg-indigo-600 text-white p-4 rounded shadow border-l-4 border-indigo-300">
                    <span className="text-[9px] uppercase opacity-50 tracking-widest font-bold">Schedule MW (Avg)</span>
                    <span className="text-xl font-black block">{((global.sch * 4) / global.blks).toFixed(2)} MW</span>
                  </div>
                  <div className="bg-indigo-600 text-white p-4 rounded shadow border-l-4 border-emerald-300">
                    <span className="text-[9px] uppercase opacity-50 tracking-widest font-bold">Avg Actual Power</span>
                    <span className="text-xl font-black block">{((global.act * 4) / global.blks).toFixed(2)} MW</span>
                  </div>
                  <div className="bg-white border-2 border-indigo-100 p-4 rounded shadow flex flex-col justify-center">
                    <span className="text-[9px] uppercase text-slate-400 font-bold tracking-widest">Net Financial Status</span>
                    <span className={`text-xl font-black ${(global.r - global.p) >= 0 ? 'text-teal-600' : 'text-rose-600'}`}>{formatINR(global.r - global.p)}</span>
                    <span className="text-xs font-bold text-slate-500">{((global.r - global.p)/100000).toFixed(2)} Lakhs</span>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center p-20 bg-white rounded-lg border-2 border-dashed border-slate-200">
              <p className="text-slate-400">Please upload a WRPC Excel file to begin analytics</p>
            </div>
          )
        } />
        <Route path="/DashboardPage" element={<DashboardPage/>} />
        <Route path="/iex" element={<iex/>} />
      </Routes>
    </div>
  );
}
